/**
 * TcmCutoverPlaybook — in-page, step-by-step reference for converting an
 * existing scan-only tenant to Hybrid from /tcm-lab.
 *
 * Rendered above the lab's section tabs (see TcmLabPage) so a Platform
 * Admin can open it and work the controls on any tab while following the
 * steps. Collapsible; the open/closed choice persists in localStorage so
 * routine diagnostic visits aren't cluttered.
 *
 * This mirrors the authoritative SOP — .agents/SOP/tcm-tenant-cutover.md.
 * If the cutover sequence changes, update BOTH (the SOP is the source of
 * truth; this is the at-a-glance operator copy).
 *
 * Carbon: a layer-01 tile whose header is a disclosure button, the steps as
 * a structured list (a tabular step number, the action at 600, a tag naming
 * the tab, the detail in secondary text) and the gated-button caveat as an
 * info notification. Voice: bare-noun operator register, matching the rest
 * of the lab. UI copy never names the vendor pipelines: the Microsoft
 * monitor pipeline is "the continuous watch" and the PowerShell export is
 * "the baseline scan" (blueprint §5.1 rule 7).
 */

import { useState } from 'react'
import { CodeSnippet } from '@/components/carbon/CodeSnippet'
import { IconChevronDown } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Tag } from '@/components/carbon/Tag'

const STORAGE_KEY = 'tcm-lab.cutover-playbook.open'

interface PlaybookStep {
  /** Which lab tab the step happens on. */
  where: string
  /** Short imperative — the actual click. */
  action: string
  /** One or two sentences of detail / gotchas. */
  detail: string
  /** A wait step renders with a "Wait" tag, not a click. */
  wait?: boolean
}

const STEPS: ReadonlyArray<PlaybookStep> = [
  {
    where: 'Enablement',
    action: 'Enable shadow ingest',
    detail:
      'Turns on continuous-watch ingestion for this tenant. Nothing ingests until it is on, so do it before you build the monitor.',
  },
  {
    where: 'Snapshots',
    action: 'Run a discovery snapshot',
    detail:
      'Snapshots tab, then New snapshot, then “Discovery (all available)”. It sizes this tenant’s monitor budget. Directory inventory (users, groups and the like) is greyed out and excluded: it is never monitored and would burn the monthly snapshot budget. One broad snapshot is enough, so don’t re-run it.',
  },
  {
    where: 'Monitors',
    action: 'Build the monitor',
    detail:
      'Monitors tab, then Force refresh. Pick the Security Essentials base (it fits within 200 instances with headroom on every tenant so far), check the budget meter is under the ceiling, then build. This is the active monitor the baseline forks from.',
  },
  {
    where: 'Ingestion',
    action: 'Wait about 12 hours for ingestion',
    detail:
      'Ingestion runs every 6 hours. Promote needs 12 hours or more since the tenant went Enabled, and a monitor that advanced in the last 12 hours. Watch progress on the Ingestion tab.',
    wait: true,
  },
  {
    where: 'Reconciliation',
    action: 'Reconcile the scan against the watch',
    detail:
      'Coverage breadth must read “Gate passes” (no blocking gaps). Walk the baseline-scan-only list and waive any type that is genuinely clean under the fresh baseline (a reason is required). Note the pre-cutover count of scan drift outside SharePoint and OneDrive: the promote retires exactly that many.',
  },
  {
    where: 'Enablement',
    action: 'Promote to Hybrid',
    detail:
      'Promote to Hybrid, then confirm. It flips the tenant to Hybrid, retires the scan drift outside SharePoint and OneDrive as a “migrated” supersession, and the customer starts seeing continuous-watch drift. SharePoint and OneDrive stay on the baseline scan permanently.',
  },
]

export default function TcmCutoverPlaybook() {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false
      return window.localStorage.getItem(STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })

  const toggle = () => {
    setOpen(prev => {
      const next = !prev
      try {
        window.localStorage?.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        /* localStorage unavailable (private mode / SSR) — non-fatal */
      }
      return next
    })
  }

  return (
    <section className="min-w-0 bg-layer-1" aria-labelledby="tcm-cutover-playbook-heading">
      <h2 className="m-0">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="tcm-cutover-playbook-body"
          className="flex min-h-12 w-full items-center gap-4 px-4 py-3 text-left transition-colors duration-110 ease-carbon hover:bg-layer-hover"
        >
          <span id="tcm-cutover-playbook-heading" className="min-w-0 flex-1 text-body font-semibold text-text-primary">
            Convert an existing tenant to Hybrid
          </span>
          <span className="flex-none text-helper text-text-helper">{open ? 'Hide' : 'Show'} the {STEPS.length} steps</span>
          <IconChevronDown
            className={`flex-none text-text-primary transition-transform duration-150 ease-carbon ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </h2>

      {open && (
        <div id="tcm-cutover-playbook-body" className="border-t border-line-subtle px-4 pb-6 pt-4">
          {/* Prerequisite */}
          <p className="m-0 max-w-[80ch] text-body text-text-secondary">
            <b className="font-semibold text-text-primary">Before you start:</b> the tenant must read Enabled on the
            Enablement tab. Generate the script, have the customer Global Admin run it, paste the payload, then
            verify. A transient 504 on Verify is safe: click Verify again with the same payload, with no need to
            re-run the script.
          </p>

          {/* Ordered steps — a structured list. */}
          <ol className="m-0 mt-4 list-none border-b border-line-subtle p-0">
            {STEPS.map((step, i) => (
              <li
                key={`${step.where}-${step.action}`}
                className="grid grid-cols-[2rem_minmax(0,1fr)] gap-x-3 border-t border-line-subtle py-3"
              >
                <span aria-hidden className="pt-px text-helper tabular-nums text-text-helper">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-body font-semibold text-text-primary">{step.action}</span>
                    <Tag kind="outline" size="sm">
                      {step.where} tab
                    </Tag>
                    {step.wait ? <Tag size="sm">Wait</Tag> : null}
                  </div>
                  <p className="m-0 mt-1 max-w-[80ch] text-body text-text-secondary">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* The gated-button caveat — the #1 source of operator confusion. */}
          <InlineNotification kind="info" title="The Promote button shows early." className="mt-6">
            “Promote to Hybrid” appears as soon as the tenant is Enabled, but the server runs all six pre-flight
            gates. Clicking before steps 1 to 5 are done returns a 409 that names what is still missing (for
            example “Insufficient ingestion history” or “No active monitor”). That is expected, not an error:
            finish the steps, then promote.
          </InlineNotification>

          {/* Post-cutover watch */}
          <p className="m-0 mt-6 max-w-[80ch] text-body text-text-secondary">
            <b className="font-semibold text-text-primary">After promote, watch for 72 hours:</b> the Compliance
            score shouldn’t swing more than about 20 points, new continuous-watch drift should get risk and
            severity enrichment within a minute, and the logs should stay clean. Roll back (a SQL flip to{' '}
            <CodeSnippet kind="inline" code="DSC" /> and a Container restart) if a signal goes bad.
          </p>

          {/* Born-Hybrid note + SOP pointer */}
          <p className="m-0 mt-4 max-w-[80ch] border-t border-line-subtle pt-4 text-helper text-text-helper">
            Net-new tenants are born Hybrid: there is no scan history to reconcile, so they skip the reconciliation
            review and the supersession close-out (steps 1 to 4 and the promote still apply). Full procedure:{' '}
            <CodeSnippet kind="inline" code=".agents/SOP/tcm-tenant-cutover.md" />.
          </p>
        </div>
      )}
    </section>
  )
}
